import axios from 'services/axios'
import { CURRENCY_CONFIG } from 'config/currency'

export interface Currency {
  officialName: string
  code: string
  symbol: string
}

export interface CurrencyExchange {
  baseCurrency: string
  targetCurrency: string
  values: Record<string, number>
}

export interface CurrencyApiResponse {
  officialName: string
  code: string
  symbol: string
}

export interface CurrencyExchangeResponse {
  base_currency: string
  target_currency: string
  rates: Record<string, number>
}

class CurrencyApiService {
  private transformCurrencyResponse(response: CurrencyApiResponse): Currency {
    return {
      officialName: response.officialName,
      code: response.code,
      symbol: response.symbol,
    }
  }

  private transformExchangeResponse(response: CurrencyExchangeResponse): CurrencyExchange {
    return {
      baseCurrency: response.base_currency,
      targetCurrency: response.target_currency,
      values: response.rates,
    }
  }

  async getCurrencies(currencyCodes?: string[]): Promise<Currency[]> {
    try {
      const params = currencyCodes && currencyCodes.length > 0 ? { currency_codes: currencyCodes } : {}

      const { data } = await axios.get('currency/currencies', { params })

      if (Array.isArray(data)) {
        return data.map(this.transformCurrencyResponse)
      }

      console.warn('Backend proxy returned non-array data:', data)
      return []
    } catch (error: any) {
      console.error('Failed to fetch currencies from backend proxy:', error)
      if (error.response) {
        console.error('Response status:', error.response.status)
        console.error('Response data:', error.response.data)
      }
      throw new Error('Failed to fetch currencies')
    }
  }

  async getCurrency(currencyCode: string): Promise<Currency | null> {
    try {
      const { data } = await axios.get(`currency/currencies/${currencyCode}`)
      return this.transformCurrencyResponse(data)
    } catch (error) {
      console.error(`Failed to fetch currency ${currencyCode}:`, error)
      return null
    }
  }

  async getExchangeRates(currencyCode: string, startYear?: number, endYear?: number): Promise<CurrencyExchange | null> {
    try {
      const params: Record<string, any> = {}
      if (startYear) params.start_year = startYear
      if (endYear) params.end_year = endYear

      const { data } = await axios.get(`currency/currencies/${currencyCode}/exchange`, { params })
      return this.transformExchangeResponse(data)
    } catch (error) {
      console.error(`Failed to fetch exchange rates for ${currencyCode}:`, error)
      return null
    }
  }

  async getAllCurrencies(): Promise<Currency[]> {
    return this.getCurrencies()
  }
}

export default new CurrencyApiService()
